import { type Plan, type Tier } from '../../repositories';
import { type PriceAsVO } from '../plan-group';
import type { EventsV1PlanTypesEnum, SocialStarterPlanTypesEnum } from '../plan-group/enums';
import { PlanTypesEnum } from '../plan-group/enums';
import { PriceVO } from '../price';

const UNLIMITED_TIER_MAX = -1;

export type PriceAsVOTier = PriceAsVO<Tier>;

const PAID_3PL_PLANS = [PlanTypesEnum.Bronze, PlanTypesEnum.Silver, PlanTypesEnum.Gold] as (
  | PlanTypesEnum
  | SocialStarterPlanTypesEnum
  | EventsV1PlanTypesEnum
)[];

export class EmailPlan {
  private tiers: PriceAsVOTier[];

  constructor(private plan: Plan, private readonly isSocialStarterGroup = false) {
    // Transform tier prices from number to PriceVO
    this.tiers = plan.tiers.map((tier) => ({
      ...tier,
      price: new PriceVO(tier.price),
    }));
  }

  public getId(): number {
    return this.plan.id;
  }

  public getPlanName(): string {
    if (this.isSocialStarterGroup) {
      // For Social Starter users, strip the "Social " prefix from plan names (e.g. "Social Lite" -> "Lite")
      return this.plan.planName.replace(/^Social\s+/i, '');
    }

    return this.plan.planName;
  }

  public getIsRecommendedPlan(): boolean {
    return this.plan.recommendedPlan;
  }

  public getTiers(): PriceAsVOTier[] {
    return this.tiers;
  }

  public getFirstTier(): PriceAsVOTier | undefined {
    return this.tiers[0];
  }

  public getTierByContactsAmount(amount: number): PriceAsVOTier | undefined {
    return this.tiers.find(
      (tier) => tier.tierMin <= amount && (tier.tierMax >= amount || tier.tierMax === UNLIMITED_TIER_MAX)
    );
  }

  public getType(): Plan['type'] {
    return this.plan.type;
  }

  public getPriceForContacts(contactsAmount: number): PriceVO | undefined {
    const priceTier = this.tiers.find(
      (tier) => tier.tierMin <= contactsAmount && (tier.tierMax >= contactsAmount || tier.tierMax === -1)
    );

    return priceTier?.price;
  }

  public getPriceTiersBelowThreshold(threshold = 50000): PriceAsVOTier[] {
    return this.tiers.filter((tier) => tier.tierMin <= threshold);
  }

  public isUnlimitedTier(tier: PriceAsVOTier): boolean {
    return tier.tierMax === UNLIMITED_TIER_MAX;
  }

  public isLite(): boolean {
    return this.plan.type === PlanTypesEnum.Bronze;
  }

  public isStandard(): boolean {
    return this.plan.type === PlanTypesEnum.Silver;
  }

  public isPremium(): boolean {
    return this.plan.type === PlanTypesEnum.Gold;
  }

  public isHidden(): boolean {
    return this.plan.displayOnPay === false;
  }

  public isDisplayableOnPlansPage({ includeNon3pl }: { includeNon3pl: boolean }) {
    if (includeNon3pl) {
      return true;
    }

    return PAID_3PL_PLANS.includes(this.plan.type);
  }
}
